import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import colors from "theme/colors";
import sizes from "theme/sizes";
import Button from "components/_UI/Button";
import Fieldset from "components/_UI/Fieldset";
import Text from "components/_UI/Text";
import { FullyCentered, HorizontalCenter } from "styled/alignment/Center";
import { Column } from "styled/alignment/Column";
import { StyledForm } from "styled/UI/StyledForm";
import signInTexts from "texts/general/sign-in";
import Divider from "components/_UI/Divider";
import Icon from "components/_UI/Icon";
import breakpoints from "theme/breakpoints";
import { useStepRegister } from "context/login/StepRegister";
import loginApi from "api/requests/login";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import Loader from "components/_UI/Loader";
import fieldValidations from "helpers/fieldValidations";
import paths from "routes/paths";
import { useUser } from "context/user/User";
import buttonText from "texts/buttons/buttonText";
import ConfirmEmailModal from "./ConfirmEmailModal";
import LinkPage from "components/_UI/LinkPage";
import { Row } from "styled/alignment/Row";
import { Plus } from 'lucide-react';
import routeParams from "theme/routeParams";
import userApi from "api/requests/user";
import OnboardingTags from "../sign-up/OnboardingTags";
import CompleteNameStep from "../sign-up/CompleteNameStep";
import { GoogleLogin } from '@react-oauth/google';



function SignIn() {
  const { setStepRegister } = useStepRegister();
  const { user, setUser, getUser } = useUser();

  const errorTokens = {
    unconfirmed_email: 'unconfirmed_email',
    pending_onboarding: 'pending_onboarding'
  }

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const redirectTo = searchParams.get('redirectTo') || paths.home;

  const [confirmEmail, setConfirmEmail] = useState(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [errorMessage, setErrorMessage] = useState("");
  const [errorType, setErrorType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false)
  const [showResendEmailMessage, setShowResendEmailMessage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [buttonsLoading, setButtonsLoading] = useState(false);
  const [googleErrorMessage, setGoogleErrorMessage] = useState("");
  const [confirmPassword, setConfirPassword] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showCompleteName, setShowCompleteName] = useState(false);
  const [isLoadingResendEmail, setIsLoadingResendEmail] = useState(false);
  const [isResendEmail, setIsResendEmail] = useState(true)

  useEffect(() => {
    const confirmEmail = searchParams.get('confirmEmail');
    const loginPartner = searchParams.get('loginWithPartner');

    // Caso o SignIn monte com loginWithPartner (quando não há sessão ativa)
    if (loginPartner == 1) {
      loginWithPartner()
      return
    }

    if (confirmEmail == 1) {
      setShowConfirmModal(true);
      return
    }

  }, []);

  const getCookie = (name) => {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    return cookie ? cookie.split('=')[1] : null;
  };

  const loginWithPartner = () => {
    const token = getCookie('token');
    if (!token) return;
    localStorage.setItem("token", token);

    // Limpa o parâmetro da URL para evitar re-execução
    searchParams.delete('loginWithPartner');
    const cleanSearch = searchParams.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
    window.history.replaceState({}, '', cleanUrl);

    window.location.reload();
  }

  const login = async ({ email, password }) => {
    try {
      setIsLoading(true);

      const { data } = await loginApi.signIn({
        email: email.toLowerCase(),
        password
      });

      localStorage.setItem("token", data.accessToken);

      searchParams.delete('redirectTo');
      navigate({
        pathname: redirectTo,
        search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
      }, { replace: true });
      window.location.reload();

    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setErrorType(error?.response?.data?.type)
      setShowResendEmailMessage(false);

      if (error?.response?.data?.type === errorTokens.unconfirmed_email) {
        const userData = { email: email.toLowerCase(), password };
        setUser(userData); // Atualiza o estado para uso futuro
        resendConfirmCode(userData); // Passa o objeto direto para a função não vir vazio
        setShowConfirmModal(true);
      }

      if (error?.response?.data?.message === 'E-mail não verificado') {
        setUser({ email: email.toLowerCase(), password });
        setShowResendEmailMessage(true);
      }
    } finally {
      setIsLoading(false);
    }
  };


  const resendConfirmCode = async (userToResend) => {
    const targetUser = userToResend || user;


    if (!targetUser?.email) {
      setErrorMessage("Dados do usuário não encontrados para reenvio.");
      return;
    }

    setLoadingModal(true);
    try {
      await loginApi.resendConfirmCode(targetUser);
      setShowConfirmModal(true);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
    } finally {
      setLoadingModal(false);
      setIsLoadingResendEmail(false)
    }
  };

  const handleTagsComplete = async (tagIds) => {
    try {
      await userApi.update(user.id, { tags: tagIds });
      searchParams.delete('redirectTo');
      navigate({
        pathname: redirectTo,
        search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
      }, { replace: true });
      window.location.reload();
    } catch (error) {
      setGoogleErrorMessage(error?.response?.data?.message || 'Erro ao salvar preferências');
    }
  };

  const handleNameComplete = async ({ first_name, last_name }) => {
    await userApi.update(user.id, { first_name, last_name });
    setShowCompleteName(false);
    setShowPreferences(true);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) {
      setGoogleErrorMessage('Erro ao entrar com Google');
      return;
    }
    setGoogleErrorMessage("");
    setButtonsLoading(true);
    try {
      const { data } = await loginApi.withGoogle({ credential: credentialResponse.credential });
      localStorage.setItem('token', data.accessToken);

      if (data.isNewUser) {
        const fetchedUser = await getUser();
        if (!fetchedUser) {
          setGoogleErrorMessage('Erro ao carregar dados do usuário. Tente novamente.');
          setButtonsLoading(false);
          return;
        }
        setButtonsLoading(false);
        if (fetchedUser.profile_completed === false) {
          setShowCompleteName(true);
        } else {
          setShowPreferences(true);
        }
        return;
      }

      searchParams.delete('redirectTo');
      navigate({
        pathname: redirectTo,
        search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
      }, { replace: true });
      window.location.reload();
    } catch (error) {
      setGoogleErrorMessage(error?.response?.data?.message || 'Erro ao entrar com Google');
    } finally {
      setButtonsLoading(false);
    }
  };

  const startSignUp = () => {
    setStepRegister(1)
    navigate({
      pathname: paths.register,
      search: new URLSearchParams(Object.fromEntries(searchParams)).toString()
    })
  }

  return (
    <Container>
      <BackButton>
        <StyledBackButton onClick={() => navigate(paths.home)}>
          <Icon name='back' color={colors.night} />
        </StyledBackButton>
      </BackButton>
      <BoxContainer fullyCentralized disabled={loadingModal}>
        {showCompleteName ? (
          <CompleteNameStep onComplete={handleNameComplete} hideBack />
        ) : showPreferences ? (
          <OnboardingTags onComplete={handleTagsComplete} hideBack />
        ) : (
          <>
            <LogoContainer>
              <Icon name={"whizr-logo"} color={colors.primary} />
            </LogoContainer>

            <StyledForm onSubmit={handleSubmit(login)}>
              <Fieldset
                placeholder={signInTexts.inputs.username}
                badgeIcon={"avatar"}
                badgePosition={"left"}
                name="email"
                register={register}
                validations={fieldValidations.email()}
              />
              {errors.email?.message && (
                <HorizontalCenter>
                  <Text color={colors.danger}> {errors.email?.message} </Text>
                </HorizontalCenter>
              )}

              <Fieldset
                placeholder={signInTexts.inputs.password}
                badgeIcon={"lock"}
                badgePosition={"left"}
                badgeIconPassword
                inputType="password"
                marginTop={sizes.big}
                badgeIconColorPassword={colors.night}
                badgeIconPositionPassword={'right'}
                name="password"
                register={register}
                validations={fieldValidations.passwordWithoutPatterns()}
              />
              {errors.password?.message && (
                <HorizontalCenter>
                  <Text color={colors.danger}> {errors.password?.message} </Text>
                </HorizontalCenter>
              )}

              {!isLoading ? (
                <Button
                  variant={buttonsLoading ? "disabled" : "primary"}
                  size="large"
                  marginTop={sizes.normal}
                  type="submit"
                >
                  {signInTexts.buttons.enter}
                </Button>
              ) : (
                <Loader />
              )}

              {errorMessage && (
                <HorizontalCenter marginTop={sizes.big}>
                  <Text type={'bold'} color={colors.danger}> {errorMessage} </Text>
                </HorizontalCenter>
              )}
            </StyledForm>



            {!buttonsLoading && (errorType !== errorTokens.pending_onboarding && errorType !== errorTokens.unconfirmed_email) && (
              <>
                <Text name="small">{signInTexts.texts.forgotPassword}</Text>
                <Button path={paths.recoverAccount} size="small" category="outline">
                  {signInTexts.texts.restoreAccount}
                </Button>
              </>
            )}

            <Divider />

            {buttonsLoading ? (
              <Loader />
            ) : (
              <>
                <Column fullyCentralized marginTop={sizes.medium}>
                  <Text name="small">{signInTexts.texts.dontHaveAccount}</Text>
                  <Button
                    variant="secondary"
                    size="large"
                    marginTop={sizes.nano}
                    onClick={startSignUp}
                  >
                    <Plus /> &nbsp;&nbsp;
                    {signInTexts.buttons.newAccount}
                  </Button>
                </Column>
                <ContentLoginGoogle>
                  <GoogleLogin
                    shape="pill"
                    type="standard"
                    heme="outline" // ou "filled_blue"
                    size="large"
                    onSuccess={handleGoogleSuccess}
                    onError={() => setGoogleErrorMessage('Erro ao entrar com Google')}
                  />
                </ContentLoginGoogle>

                {googleErrorMessage && (
                  <HorizontalCenter marginTop='14px'>
                    <Text color={colors.danger}> {googleErrorMessage} </Text>
                  </HorizontalCenter>
                )}

                <LinkCreatePartner to={'/quero-ser-parceiro'} target={'_blank'}>
                  Quer ser um parceiro do Whizr?
                </LinkCreatePartner>

                <Text name="small" marginTop={'20px'} textAlign={'center'}>
                  Ao continuar você concorda com os <LinkTerms to={`${paths.terms.useTerms}`}>Termos de Uso</LinkTerms> e <LinkTerms to={`${paths.terms.policiePrivacy}`}>Política de Privacidade.</LinkTerms>
                </Text>

              </>
            )}

            {showConfirmModal && (
              <ConfirmEmailModal
                fromSignIn={true}
                setShowConfirmModal={setShowConfirmModal}
                onLogged={(e) => login(e)}
                resendConfirmCode={() => {
                  resendConfirmCode();
                  setIsLoadingResendEmail(true);
                }}
                isLoadingResendEmail={isLoadingResendEmail}
                isResendEmail={isResendEmail}
                setIsResendEmail={setIsResendEmail}
              />
            )}
          </>
        )}

      </BoxContainer>
    </Container>
  );
}

const Container = styled(Column)`
  align-items: center;
`

const BoxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 30px 40px 0px 40px;
  background: ${colors.neutralDark};
    ${({ disabled }) => disabled && css`
        pointer-events: none;
    `}

  @media screen and (min-width: 375px) {
    width: 420px;
  }
  @media screen and (min-width: ${breakpoints.lg}) {
    border-radius: 32px 0px 0px 32px;
  }

`;

const LogoContainer = styled.div`
  width: 110px;
  height: 110px;
  margin-bottom: ${sizes.gigantic};

  // @media screen and (min-width: ${breakpoints.lg}) {
  //     display: none;
  // }
`;

const LinkCreatePartner = styled(Link)`
  text-decoration: underline;
  color: ${colors.primary};
  margin-top: 8px;
  font-size: 12px;
  cursor: pointer;
`
const LinkTerms = styled(Link)`
  text-decoration: underline;
  color: ${colors.primary};
  font-size: 12px;
`;

const BackButton = styled(Row)`
  width: 100%;
  height: 40px;
  padding: 10px;
  margin-top: 40px;
  /* background: red; */
  align-items: center;
`;



const StyledBackButton = styled(Row)`
  width: 40px;
  padding: 10px;
  cursor: pointer;
  @media screen and (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const ContentLoginGoogle = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 50px !important;
  margin-bottom: 5px;
  & > div {
    width: 100%;
    height: 50px;
    border-radius: 50px !important;
    & > div > div > div {
      height: 50px;
      border: 1px solid rgb(255, 255, 255);
      color: rgb(63, 107, 198);
     
      border-radius: 50px;
      box-shadow: rgb(172, 172, 172) 0px 2px 3px !important;
      border-radius: 50px !important;
      & > div > {
        box-shadow: rgb(172, 172, 172) 0px 2px 3px !important;
        &:nth-child(2) {
          font-weight: 600 !important;
        }
      }
    }
  }
`

export default SignIn;

